import { Button, Checkbox, Dialog, Heading, Pane, toaster } from 'evergreen-ui';
import moment from 'moment';
import { Component } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Api from '../../../util/Api';
import { createEvent, deleteEvent, duplicateEvent, getEvents, publishMonth, updateEvent } from '../actions/EventActions';
import './CalendarView.css';
import EventUnifiedModal from './EventUnifiedModal';

// Set moment locale to start weeks on Monday
moment.updateLocale('en', {
  week: {
    dow: 1, // Monday is the first day of the week
  }
});

const localizer = momentLocalizer(moment);

class CalendarView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showEventModal: false,
      selectedEvent: null,
      selectedSlot: null,
      showDeleteDialog: false,
      eventToDelete: null,
      currentDate: new Date(),
      isDuplicating: false,
      isCreating: false,
      deleting: false,
      selectedLocation: 'KRIT Ploiesti', // Default location
      showPublishDialog: false,
      publishing: false,
      showPdfOptionsModal: false,
      downloadingPdf: false,
      pdfOptions: {
        headerSpace: false,
        sideSpace: false,
        selectedDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true
        }
      }
    };
  }

  componentDidMount() {
    this.loadEvents();
  }

  loadEvents = () => {
    // Calculate the first and last visible days in the calendar grid
    const currentDate = moment(this.state.currentDate);
    const firstDayOfMonth = currentDate.clone().startOf('month');
    const lastDayOfMonth = currentDate.clone().endOf('month');

    // Get the day of week for the first day (0=Sunday, 1=Monday, etc in moment)
    const firstDayOfWeek = firstDayOfMonth.day();
    // Convert to Monday-based (Monday=1, Sunday=7)
    const firstDayOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    // Start from the Monday of the week containing the first day
    const startDate = firstDayOfMonth.clone().subtract(firstDayOffset, 'days').format('YYYY-MM-DD');

    // Calculate total cells needed
    const daysInMonth = lastDayOfMonth.date();
    const totalCells = Math.ceil((daysInMonth + firstDayOffset) / 7) * 7;

    // End date is the last visible day in the grid
    const endDate = firstDayOfMonth.clone()
      .subtract(firstDayOffset, 'days')
      .add(totalCells - 1, 'days')
      .endOf('day')
      .format('YYYY-MM-DD HH:mm:ss');

    this.props.getEvents({ startDate, endDate, location: this.state.selectedLocation });
  };

  handleLocationToggle = () => {
    this.setState((prevState) => ({
      selectedLocation: prevState.selectedLocation === 'KRIT Ploiesti' ? 'KRIT Brasov' : 'KRIT Ploiesti'
    }), () => {
      this.loadEvents();
    });
  };

  handleDownloadPDF = async () => {
    const date = moment(this.state.currentDate).format('YYYY-MM');
    const location = this.state.selectedLocation;
    const { headerSpace, sideSpace, selectedDays } = this.state.pdfOptions;

    // Convert selected days object to comma-separated string
    const daysParam = Object.keys(selectedDays).filter(day => selectedDays[day]).join(',');

    this.setState({ downloadingPdf: true });

    try {
      // Use Api utility with responseType blob for file download
      const blob = await Api.get(
        `/events/pdf/generate`,
        {},
        {
          date,
          location,
          headerSpace,
          sideSpace,
          days: daysParam
        },
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calendar-${location.replace(/\s+/g, '-')}-${date}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Close modal
      this.setState({ showPdfOptionsModal: false, downloadingPdf: false });

      toaster.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toaster.danger('Failed to download PDF');
      this.setState({ downloadingPdf: false });
    }
  };

  handleOpenPublishDialog = () => {
    this.setState({ showPublishDialog: true });
  };

  handleClosePublishDialog = () => {
    if (!this.state.publishing) {
      this.setState({ showPublishDialog: false });
    }
  };

  handlePublishMonth = async () => {
    const currentDate = moment(this.state.currentDate);
    const startDate = currentDate.clone().startOf('month').format('YYYY-MM-DD HH:mm:ss');
    const endDate = currentDate.clone().endOf('month').format('YYYY-MM-DD HH:mm:ss');
    const location = this.state.selectedLocation;

    this.setState({ publishing: true });

    try {
      const result = await this.props.publishMonth({ startDate, endDate, location });
      const count = result && result.published ? result.published : 0;

      if (count === 0) {
        toaster.notify('Nu exista evenimente draft de publicat in aceasta luna');
      } else {
        toaster.success(count === 1 ? 'Un eveniment publicat' : `${count} evenimente publicate`);
      }

      this.setState({ showPublishDialog: false, publishing: false });
      await this.loadEvents();
    } catch (error) {
      console.error('Failed to publish month:', error);
      toaster.danger('Evenimentele nu au putut fi publicate');
      this.setState({ publishing: false });
    }
  };

  handleOpenPdfModal = () => {
    this.setState({ showPdfOptionsModal: true });
  };

  handleClosePdfModal = () => {
    this.setState({ showPdfOptionsModal: false, downloadingPdf: false });
  };

  handlePdfOptionChange = (option, value) => {
    this.setState(prevState => ({
      pdfOptions: {
        ...prevState.pdfOptions,
        [option]: value
      }
    }));
  };

  handleDayToggle = (day) => {
    this.setState(prevState => ({
      pdfOptions: {
        ...prevState.pdfOptions,
        selectedDays: {
          ...prevState.pdfOptions.selectedDays,
          [day]: !prevState.pdfOptions.selectedDays[day]
        }
      }
    }));
  };

  handleNavigate = (date) => {
    this.setState({ currentDate: date }, () => {
      this.loadEvents();
    });
  };

  get events() {
    return (this.props.events || []).map(event => ({
      ...event,
      start: new Date(event.start),
      end: new Date(event.end),
    }));
  }

  handleSelectSlot = (slotInfo) => {
    this.setState({
      showEventModal: true,
      selectedSlot: slotInfo,
      selectedEvent: null,
      isCreating: true,
      isDuplicating: false,
    });
  };

  handleSelectEvent = (event) => {
    this.setState({
      showEventModal: true,
      selectedEvent: event,
      selectedSlot: null,
      isCreating: false,
      isDuplicating: false,
    });
  };

  handleDuplicateEvent = () => {
    this.setState({
      isDuplicating: true,
      isCreating: false,
    });
  };

  handleCloseModal = () => {
    this.setState({
      showEventModal: false,
      selectedEvent: null,
      selectedSlot: null,
      isDuplicating: false,
      isCreating: false,
    });
  };

  handleSaveEvent = async (eventData) => {
    try {
      if (eventData.id && !this.state.isDuplicating) {
        // Update existing event
        await this.props.updateEvent(eventData.id, eventData);
        // Reload events list
        await this.loadEvents();
      } else if (this.state.isDuplicating) {
        // Duplicate mode can fan out into several copies (one per chosen start date)
        const eventsToCreate = Array.isArray(eventData) ? eventData : [eventData];
        let created = 0;
        for (const data of eventsToCreate) {
          const newEvent = await this.props.duplicateEvent(data, { silent: true });
          if (newEvent && newEvent.id) created += 1;
        }
        toaster.success(created === 1 ? 'Eveniment duplicat' : `${created} evenimente create`);
        this.setState({
          showEventModal: false,
          selectedEvent: null,
          isDuplicating: false,
          isCreating: false,
        });
        await this.loadEvents();
        return;
      } else {
        // Add new event
        await this.props.createEvent(eventData);
        // Reload events list
        await this.loadEvents();
      }
    } catch (error) {
      // Error will be handled by the action and shown via toaster
      console.error('Failed to save event:', error);
      throw error;
    }
  };

  handleDeleteClick = (event) => {
    this.setState({
      showDeleteDialog: true,
      eventToDelete: event,
    });
  };

  handleConfirmDelete = async () => {
    if (this.state.eventToDelete) {
      this.setState({ deleting: true });
      try {
        await this.props.deleteEvent(this.state.eventToDelete.id);

        // Reload events after deletion
        await this.loadEvents();

        this.setState({
          showDeleteDialog: false,
          eventToDelete: null,
          showEventModal: false,
          selectedEvent: null,
          deleting: false,
        });
      } catch (error) {
        console.error('Failed to delete event:', error);
        this.setState({ deleting: false });
      }
    }
  };

  handleCancelDelete = () => {
    this.setState({
      showDeleteDialog: false,
      eventToDelete: null,
    });
  };

  // Custom event component for month view
  CustomEvent = ({ event }) => {
    const CONTENT_URL = process.env.REACT_APP_CONTENT_URL || 'https://content.krit.ro/';
    const S3_PATH = 'public-assets/event-images/';

    const getImageUrl = (imagePath, useThumb = false) => {
      if (!imagePath) return null;
      if (imagePath.startsWith('http')) return imagePath;
      if (imagePath.startsWith('data:')) return imagePath;
      // imagePath is now just UUID+extension, e.g., "abc123.png" or "def456.jpg"
      const prefix = useThumb ? 'thumb_' : 'default_';
      return `${CONTENT_URL}${S3_PATH}${prefix}${imagePath}`;
    };

    const formatTime = (date) => {
      return moment(date).format('HH:mm');
    };

    // Get status color
    const getStatusColor = (status) => {
      switch(status) {
        case 'published': return '#3366FF'; // blue
        case 'open': return '#47B881'; // green
        case 'cancelled': return '#8F95B2'; // gray
        case 'draft':
        default: return '#F7B238'; // orange
      }
    };

    const logoUrl = getImageUrl(event.imageLogo, true); // Use thumb for logo
    const backgroundUrl = getImageUrl(event.imageCalendarBackground, true); // Use thumb for background
    const statusColor = getStatusColor(event.status || 'draft');

    return (
      <div className="custom-event-wrapper" style={{ position: 'relative' }}>
        {/* Status indicator bar */}
        <div style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '4px',
          backgroundColor: statusColor,
          zIndex: 10
        }} />
        <div
          className="custom-event-thumbnail"
          style={{ backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none' }}
        >
          {logoUrl && (
            <div className="custom-event-logo" style={{ backgroundImage: `url(${logoUrl})` }} />
          )}
        </div>
        <div className="custom-event-content">
          <div className="custom-event-title">{event.title}</div>
          <div className="custom-event-details">
            <span className="custom-event-time">{formatTime(event.start)}</span>
            {event.capacity && (
              <span className="custom-event-capacity">
                ({event.reservations ? event.reservations.filter(r => r.status !== 'cancelled').length : 0}/{event.capacity})
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Custom component for month date header with add button
  CustomMonthDateHeader = ({ date, label }) => {
    return (
      <div className="calendar-day-cell">
        <span>{label}</span>
        <button
          className="add-event-btn"
          onClick={(e) => {
            e.stopPropagation();
            const endDate = moment(date).add(1, 'hour').toDate();
            this.handleSelectSlot({ start: date, end: endDate });
          }}
          title="Add event"
          type="button"
        >
          +
        </button>
      </div>
    );
  };

  render() {
    return (
      <Pane>
        <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={24}>
          <Heading size={700} color="#234361">
            Event Calendar
          </Heading>
          <Pane display="flex" gap={8} alignItems="center">
            <Link to="/event-reservations">
              <Button
                appearance="default"
                iconBefore="calendar"
              >
                Rezervari Evenimente
              </Button>
            </Link>
            <Button
              appearance="default"
              intent="success"
              onClick={this.handleOpenPublishDialog}
              iconBefore="upload"
            >
              Publica luna
            </Button>
            <Button
              appearance="default"
              onClick={this.handleOpenPdfModal}
              iconBefore="download"
            >
              Download PDF
            </Button>
            <Pane display="flex" gap={8} backgroundColor="#F4F5F9" padding={4} borderRadius={6}>
              <Button
                appearance={this.state.selectedLocation === 'KRIT Ploiesti' ? 'primary' : 'default'}
                onClick={() => {
                  if (this.state.selectedLocation !== 'KRIT Ploiesti') {
                    this.setState({ selectedLocation: 'KRIT Ploiesti' }, this.loadEvents);
                  }
                }}
              >
                KRIT Ploiesti
              </Button>
              <Button
                appearance={this.state.selectedLocation === 'KRIT Brasov' ? 'primary' : 'default'}
                onClick={() => {
                  if (this.state.selectedLocation !== 'KRIT Brasov') {
                    this.setState({ selectedLocation: 'KRIT Brasov' }, this.loadEvents);
                  }
                }}
              >
                KRIT Brasov
              </Button>
            </Pane>
          </Pane>
        </Pane>
        <div style={{
          minHeight: '700px',
          backgroundColor: 'white',
          borderRadius: '3px',
          padding: '20px',
          boxShadow: '0 0 0 1px rgba(67, 90, 111, 0.14), 0 1px 3px 0 rgba(67, 90, 111, 0.12)'
        }}>
          <Calendar
            localizer={localizer}
            events={this.events}
            startAccessor="start"
            endAccessor="end"
            selectable={false}
            onSelectEvent={this.handleSelectEvent}
            onNavigate={this.handleNavigate}
            views={['month']}
            defaultView="month"
            popup
            popupOffset={30}
            doShowMoreDrillDown={false}
            components={{
              month: {
                dateHeader: this.CustomMonthDateHeader,
                event: this.CustomEvent,
              }
            }}
            style={{
              height: 'auto',
            }}
          />
        </div>

        <EventUnifiedModal
          open={this.state.showEventModal}
          event={this.state.selectedEvent}
          selectedSlot={this.state.selectedSlot}
          isDuplicating={this.state.isDuplicating}
          isCreating={this.state.isCreating}
          onSave={this.handleSaveEvent}
          onClose={this.handleCloseModal}
          onDelete={this.state.selectedEvent && !this.state.isDuplicating ? () => this.handleDeleteClick(this.state.selectedEvent) : null}
          onDuplicate={this.handleDuplicateEvent}
          onStatusChange={this.loadEvents}
        />

        <Dialog
          isShown={this.state.showDeleteDialog}
          title="Delete Event"
          onCloseComplete={this.handleCancelDelete}
          confirmLabel="Delete"
          intent="danger"
          onConfirm={this.handleConfirmDelete}
          isConfirmLoading={this.state.deleting}
          isConfirmDisabled={this.state.deleting}
          shouldCloseOnOverlayClick={!this.state.deleting}
          shouldCloseOnEscapePress={!this.state.deleting}
        >
          Are you sure you want to delete this event? This action cannot be undone.
        </Dialog>

        <Dialog
          isShown={this.state.showPublishDialog}
          title="Publica evenimentele lunii"
          onCloseComplete={this.handleClosePublishDialog}
          confirmLabel={this.state.publishing ? 'Se publica...' : 'Publica'}
          intent="success"
          onConfirm={this.handlePublishMonth}
          isConfirmLoading={this.state.publishing}
          isConfirmDisabled={this.state.publishing}
          hasCancel={true}
          cancelLabel="Anuleaza"
          shouldCloseOnOverlayClick={!this.state.publishing}
          shouldCloseOnEscapePress={!this.state.publishing}
        >
          Publici toate evenimentele in stadiu draft din {moment(this.state.currentDate).format('MMMM YYYY')} pentru {this.state.selectedLocation}? Evenimentele deja publicate raman neschimbate.
        </Dialog>

        <Dialog
          isShown={this.state.showPdfOptionsModal}
          title="PDF Download Options"
          onCloseComplete={this.handleClosePdfModal}
          confirmLabel={this.state.downloadingPdf ? "Generating PDF..." : "Download PDF"}
          onConfirm={this.handleDownloadPDF}
          isConfirmLoading={this.state.downloadingPdf}
          isConfirmDisabled={this.state.downloadingPdf}
          hasCancel={true}
          cancelLabel="Cancel"
          shouldCloseOnOverlayClick={!this.state.downloadingPdf}
          shouldCloseOnEscapePress={!this.state.downloadingPdf}
        >
          <Pane marginBottom={16}>
            <Checkbox
              label="Leave header space (for manual header)"
              checked={this.state.pdfOptions.headerSpace}
              onChange={(e) => this.handlePdfOptionChange('headerSpace', e.target.checked)}
            />
          </Pane>
          <Pane marginBottom={16}>
            <Checkbox
              label="Leave side space (for side commercials)"
              checked={this.state.pdfOptions.sideSpace}
              onChange={(e) => this.handlePdfOptionChange('sideSpace', e.target.checked)}
            />
          </Pane>
          <Pane marginBottom={8} marginTop={16}>
            <strong>Select days to include:</strong>
          </Pane>
          <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={8}>
            <Checkbox
              label="Monday"
              checked={this.state.pdfOptions.selectedDays.monday}
              onChange={() => this.handleDayToggle('monday')}
            />
            <Checkbox
              label="Tuesday"
              checked={this.state.pdfOptions.selectedDays.tuesday}
              onChange={() => this.handleDayToggle('tuesday')}
            />
            <Checkbox
              label="Wednesday"
              checked={this.state.pdfOptions.selectedDays.wednesday}
              onChange={() => this.handleDayToggle('wednesday')}
            />
            <Checkbox
              label="Thursday"
              checked={this.state.pdfOptions.selectedDays.thursday}
              onChange={() => this.handleDayToggle('thursday')}
            />
            <Checkbox
              label="Friday"
              checked={this.state.pdfOptions.selectedDays.friday}
              onChange={() => this.handleDayToggle('friday')}
            />
            <Checkbox
              label="Saturday"
              checked={this.state.pdfOptions.selectedDays.saturday}
              onChange={() => this.handleDayToggle('saturday')}
            />
            <Checkbox
              label="Sunday"
              checked={this.state.pdfOptions.selectedDays.sunday}
              onChange={() => this.handleDayToggle('sunday')}
            />
          </Pane>
        </Dialog>
      </Pane>
    );
  }
}

const mapStateToProps = (state) => ({
  events: state.calendar.events
});

const mapDispatchToProps = {
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  duplicateEvent,
  publishMonth
};

export default connect(mapStateToProps, mapDispatchToProps)(CalendarView);
